import * as types from '../constants/ActionTypes';
import * as endPoints from '../constants/EndPoints';
import * as actionsHistory from './HistoryActions';
import * as actionsSensors from './SensorsActions';
import { fetchMiddleware, setIndexes, getIconFeatures } from './MiddlewareActions';

//--------------------------------
// Receive & Request
//--------------------------------

export function receiveNoiseSamples(project, noiseSamples, noiseSamplesIcons, noiseSamplesIndexMap) {
  return {
    type: types.RECEIVE_NOISE_SAMPLES,
    project,
    noiseSamples,
    noiseSamplesIcons,
    indexMap: noiseSamplesIndexMap,
  };
}

function requestNoiseSamples(project) {
  return {
    type: types.REQUEST_NOISE_SAMPLES,
    project
  };
}

//--------------------------------
// Fetch
//--------------------------------

export function fetchNoiseSamplesIfNeeded(project, force = false) {
  return (dispatch, getState) => {
    if (shouldFetchNoiseSamples(getState(), project, force)) {
      return dispatch(fetchNoiseSamples(project));
    } else {
      // Even if we're not fetching new data, make sure color is applied correctly
      // This ensures the display updates when toggling the layer
      const state = getState();
      const colorAttr = state.local.noiseSamplesColorAttr || 'Nc1';
      return dispatch(setNoiseColorByAttribute(project, colorAttr));
    }
  };
}

function shouldFetchNoiseSamples(state, project, force) {
  const projectData = state.leaksList.leaksByProject[project];
  if (!projectData) {
    return true;
  }

  const noiseSamples = projectData.noiseSamples;
  if (force || !noiseSamples) {
    return true;
  }
  return !noiseSamples.isFetching;
}

function fetchNoiseSamples(project) {
  return (dispatch, getState) => {
    const state = getState();
    const projectData = state.leaksList.leaksByProject[project];

    if (!projectData || !projectData.sensors || !projectData.sensors.sensors || !Array.isArray(projectData.sensors.sensors)) {
      return Promise.resolve();
    }

    const sensors = projectData.sensors;

    dispatch(requestNoiseSamples(project));

    // Map sensors data to noise samples format
    const noiseSamples = sensors.sensors
      .filter(sensor => sensor.SensorStatus !== 'Not Installed') // Filter out uninstalled sensors
      .map(sensor => {
        return {
          id: sensor.id,
          deviceID: sensor.DeviceID,
          Nc1: sensor.Nc1,
          Nc3: sensor.Nc3,
          Nc3f1: sensor.Nc3f1,
          Nc3f2: sensor.Nc3f2,
          Noise: sensor.Noise,
          latitude: sensor.Latitude,
          longitude: sensor.Longitude,
          position: [sensor.Longitude, sensor.Latitude],
          NoiseAlertID: sensor.NoiseAlertID,
          NoiseAlertDate: sensor.NoiseAlertDate
        };
      });

    // Set color values
    calculateNoiseColorValue(noiseSamples, 'Nc1', 'colorNc1');
    calculateNoiseColorValue(noiseSamples, 'Nc3', 'colorNc3');
    calculateNoiseColorValue(noiseSamples, 'Nc3f1', 'colorNc3f1');
    calculateNoiseColorValue(noiseSamples, 'Nc3f2', 'colorNc3f2');
    calculateNoiseColorValue(noiseSamples, 'Noise', 'colorValue');

    // Create noise samples icons and index map
    const noiseSamplesIcons = getIconFeatures('noiseSamples', noiseSamples);

    const noiseSamplesIndexMap = setIndexes(noiseSamples, 'id');

    return Promise.resolve().then(() => {
      dispatch(receiveNoiseSamples(project, noiseSamples, noiseSamplesIcons, noiseSamplesIndexMap));

      // Make sure we apply the color attribute after loading the data
      const colorAttr = state.local.noiseSamplesColorAttr || 'Nc1';
      dispatch(setNoiseColorByAttribute(project, colorAttr));

      return { success: true };
    });
  };
}

function calculateNoiseColorValue(noiseSamples, forAttrName, destAttrName) {
  if (noiseSamples.length) {

    // console.log('noiseSamples', noiseSamples);

    let tempNoiseSample2 = noiseSamples.slice();

    //First stage - remove 10% of largest noises
    sortByProperty(tempNoiseSample2, forAttrName); //Sort by Noise
    const countToRemoveFromArray = (tempNoiseSample2.length * 0.1).toFixed(0);
    for (let i = 0; i < countToRemoveFromArray; i++) {
      tempNoiseSample2.pop();
    }

    //Second stage - calculate average
    let sum = 0;
    //let sumSqrt = 0;
    tempNoiseSample2.forEach(val => {
      sum += val[forAttrName];
      //sumSqrt += Math.sqrt(val[forAttrName]);
    });
    const average = sum / tempNoiseSample2.length;

    //Third stage - calculate variance
    //let sensorVar = 0;
    let sensorVarSqrt = 0;
    tempNoiseSample2.forEach(value => {
      //sensorVar += Math.pow((value[forAttrName] - average), 2);
      sensorVarSqrt += Math.pow((Math.sqrt(value[forAttrName]) - average), 2);
    });

    //sensorVar = sensorVar / tempNoiseSample2.length;//Maybe need sqrt
    sensorVarSqrt = sensorVarSqrt / tempNoiseSample2.length;
    //const sigma = Math.sqrt(sensorVar);
    const sigmaSqrt = Math.sqrt(sensorVarSqrt);

    let upper = average + 4 * sigmaSqrt;  //sensorVar;
    const lower = average - sigmaSqrt / 2;  //sigma / 2;   // Bori

    if (upper == lower) { // avoid dividing by zero...
      upper += 1;
    }

    //Fourth stage - create the icon according to the Noise
    noiseSamples.forEach(item => {
      let c;

      if (item[forAttrName] > (upper)) {
        //Above upper
        c = 255;
      }
      else if (item[forAttrName] < lower) {
        c = 0;
      }
      else {
        c = ((item[forAttrName] - lower) / (upper - lower)) * 255;
      }

      item[destAttrName] = Math.round(c / 10);
    });
    //noiseSamples = ARRAY;
  }
}

function sortByProperty(array, propertyName) {
  return array.sort(function (a, b) {
    return a[propertyName] - b[propertyName];
  });
}

export const closeNoiseAlert = (sensorId, noiseAlertId) => {
  return (dispatch, getState) => {
    const state = getState();
    const projectId = state.leaksList.selectedProject;
    const path = endPoints.END_POINT + '/sensor/' + sensorId + '/noiseAlert/' + noiseAlertId;

    fetchMiddleware(path, { method: 'DELETE' }, getState).then(json => {
      dispatch(fetchNoiseSamples(projectId));
      dispatch(actionsSensors.fetchSensorsIfNeeded(projectId, true));
      dispatch(actionsSensors.fetchSensorDetails(projectId, sensorId));
      dispatch(actionsHistory.fetchSensorHistoryIfNeeded(projectId, sensorId, true));
    });
  };
};

export function setNoiseColorValue(project, id, attr, value) {
  return (dispatch, getState) => {
    const state = getState();
    const projectData = state.leaksList.leaksByProject[project];

    if (!projectData || !projectData.noiseSamples) {
      return;
    }

    const noiseSamples = projectData.noiseSamples;
    const noiseSamplesIcons = noiseSamples.noiseSamplesIcons;
    const noiseSamplesIndexMap = noiseSamples.indexMap;

    if (!noiseSamplesIcons || !noiseSamplesIndexMap) {
      return;
    }

    const noiseSample = noiseSamplesIcons[id];
    if (noiseSample) {
      noiseSample[attr] = value;
      noiseSamplesIcons[id] = noiseSample;
      dispatch(receiveNoiseSamples(project, noiseSamples.noiseSamples, noiseSamplesIcons, noiseSamplesIndexMap));
    }
  };
}

export function setNoiseColorByAttribute(project, colorAttr) {
  return (dispatch, getState) => {
    const state = getState();
    const projectData = state.leaksList.leaksByProject[project];

    // First, update the noise samples color attribute in the state
    dispatch({
      type: types.SET_NOISE_SAMPLES_COLOR_ATTR,
      project,
      colorAttr
    });

    // If we don't have noise samples data yet, fetch it first
    if (!projectData || !projectData.noiseSamples || !projectData.noiseSamples.noiseSamplesIcons) {
      return dispatch(fetchNoiseSamplesIfNeeded(project, true));
    }

    const noiseSamples = projectData.noiseSamples;

    const noiseSamplesIcons = noiseSamples.noiseSamplesIcons;
    const noiseSamplesIndexMap = noiseSamples.indexMap;

    // Update the noise samples icons
    Object.keys(noiseSamplesIcons).forEach(id => {
      const noiseSample = noiseSamplesIcons[id];
      if (noiseSample && noiseSample[colorAttr] != null) {
        noiseSample.noise = noiseSample[colorAttr];
        noiseSample.value = noiseSample['color' + colorAttr];

        // Make sure to preserve the noiseAlertId and noiseAlertDate
        if (noiseSample.NoiseAlertID) {
          noiseSample.noiseAlertId = noiseSample.NoiseAlertID;
          noiseSample.noiseAlertDate = noiseSample.NoiseAlertDate;
        }
      }
    });

    // Dispatch the updated noise samples
    dispatch(receiveNoiseSamples(project, noiseSamples.noiseSamples, noiseSamplesIcons, noiseSamplesIndexMap));

    return { success: true, needsMapUpdate: true };
  };
}
